import { FormInput, FormSelect } from "@/components";
import {
  Button,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui";
import { yearOptions } from "@/lib/utils";
import { X, Trash2, Plus } from "lucide-react";
import { FC } from "react";
import { FormProvider } from "react-hook-form";
import { UpdatePdcProps } from "../../../../InstallmentSchedule/pdc/types";
import {
  GRADE_NAME,
  NO_OF_STUDENT,
  SECTION,
  SECTION_NAME,
  YEAR,
} from "./consts";
import useUpdateCurriculum from "./useUpdateCurriculum";

const AddEditCurriculum: FC<UpdatePdcProps> = ({
  onCancel,
  open,
  editData,
}) => {
  const {
    form,
    onSubmit,
    requiredFields,
    isMobile,
    gradeOptions,
    isLoading,
    isGradeDataLoading,
    fields,
    onAddMoreSection,
    onRemoveSection,
    handleCancel,
  } = useUpdateCurriculum({
    onCancel,
    editData,
  });

  const { errors } = form.formState;

  return (
    <Drawer open={open} direction={isMobile ? "bottom" : "right"}>
      <DrawerContent className="px-6 lg:min-w-[500px] xl:min-w-[730px]">
        <DrawerHeader className="px-0">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-[20px]">
              {editData ? "Update" : "Add"} Grade
            </DrawerTitle>
            <X
              onClick={handleCancel}
              className="cursor-pointer stroke-g-300 hover:stroke-g-400"
            />
          </div>
        </DrawerHeader>

        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="relative">
            <div className="relative bg-white rounded-lg pt-[30px] max-h-[70vh] lg:max-h-[95vh] overflow-y-auto pb-[130px] px-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-[25px]">
                <FormSelect
                  name={GRADE_NAME}
                  control={form.control}
                  label="Grade Name"
                  placeholder={
                    isGradeDataLoading ? "Loading..." : "Please select"
                  }
                  error={errors[GRADE_NAME]?.message}
                  isRequired={requiredFields.includes(GRADE_NAME)}
                  options={gradeOptions}
                  disabled={isGradeDataLoading}
                />
                <FormSelect
                  name={YEAR}
                  control={form.control}
                  label="Year"
                  placeholder="Please select"
                  error={errors[YEAR]?.message}
                  isRequired={requiredFields.includes(YEAR)}
                  options={yearOptions()}
                />
              </div>
              <div className="col-span-1 md:col-span-2 lg:col-span-3 bg-[#FFF5E9] gap-[25px]  mt-4">
                <div className="form-select-wrapper">
                  {fields.map((field, sectionIndex) => {
                    return (
                      <div
                        key={field.id}
                        className="grid grid-cols-1 md:grid-cols-2 gap-[20px] bg-[#FFF5E9] p-3 rounded-md items-end relative"
                      >
                        {/* Delete Button - Only show if there are multiple sections */}
                        {fields.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => onRemoveSection(sectionIndex)}
                            className="absolute top-2 right-2 h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50 z-10"
                            title="Delete section"
                          >
                            <Trash2 className="h-4 w-4 stroke-red-600" />
                          </Button>
                        )}

                        {/* School Field */}
                        <FormInput
                          name={`${SECTION}.${sectionIndex}.${SECTION_NAME}`}
                          control={form.control}
                          label="Section Name"
                          placeholder="Please select"
                          error={
                            (errors[SECTION] as any)?.[`${sectionIndex}`]?.[
                              SECTION_NAME
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${SECTION}[].${SECTION_NAME}`
                          )}
                        />

                        {/* Role Field */}
                        <FormInput
                          name={`${SECTION}.${sectionIndex}.${NO_OF_STUDENT}`}
                          control={form.control}
                          label="No of Students"
                          placeholder="Please select"
                          error={
                            (errors[SECTION] as any)?.[`${sectionIndex}`]?.[
                              NO_OF_STUDENT
                            ]?.message
                          }
                          isRequired={requiredFields.includes(
                            `${SECTION}[].${NO_OF_STUDENT}`
                          )}
                          options={[]}
                        />
                      </div>
                    );
                  })}

                  <Button
                    variant="ghost"
                    type="button"
                    className="text-primary mt-2 items-end"
                    onClick={onAddMoreSection}
                  >
                    + Add More
                  </Button>
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-3 absolute bottom-3 w-full bg-white justify-end pb-[25px] z-[3]">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? "Submitting..." : editData ? "Update" : "Submit"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DrawerContent>
    </Drawer>
  );
};

export default AddEditCurriculum;
